/**
 * League slug mapping for URL-based routing
 * Maps URL slugs (e.g., /MastersOfMusic) to league names in the database
 */

export interface LeagueSlugMapping {
  slug: string;
  leagueName: string;
  displayName: string;
}

/**
 * Mapping of URL slugs to league names
 * Add new leagues here as needed
 */
export const LEAGUE_SLUGS: LeagueSlugMapping[] = [
  {
    slug: 'MOM',
    leagueName: 'Masters of Music',
    displayName: 'Masters of Music',
  },
  {
    slug: 'BLIS',
    leagueName: 'B.L.I.S. League',
    displayName: 'B.L.I.S. League',
  },
  {
    slug: 'BLISv3',
    leagueName: 'B.L.I.S v3',
    displayName: 'B.L.I.S v3',
  },
  {
    slug: 'BLISLeague1',
    leagueName: 'B.L.I.S. League 1',
    displayName: 'B.L.I.S. League 1',
  },
  {
    slug: 'BLISLeague2',
    leagueName: 'B.L.I.S. League 2. Electric Boogaloo',
    displayName: 'B.L.I.S. League 2',
  },
];

const RESERVED_SLUGS = new Set([
  'playlists',
  'leagues',
  'login',
  'signup',
  'musicleague-transfer',
  'forgot-password',
  'reset-password',
  'dashboard',
  'admin',
  'features',
  'debug-db',
  'profile',
  'seasons',
  'about',
  'privacy',
  'terms',
  'help',
  'contact',
]);

/**
 * Get league name from slug
 */
export function getLeagueNameFromSlug(slug: string): string | null {
  const normalized = slug.trim().toLowerCase();
  const mapping = LEAGUE_SLUGS.find(m => m.slug.toLowerCase() === normalized);
  return mapping?.leagueName || null;
}

/**
 * Get slug from league name
 */
export function getSlugFromLeagueName(leagueName: string): string | null {
  const normalized = leagueName.trim().toLowerCase();
  const mapping = LEAGUE_SLUGS.find(m => m.leagueName.toLowerCase() === normalized);
  return mapping?.slug || null;
}

/**
 * Get display name from slug
 */
export function getDisplayNameFromSlug(slug: string): string | null {
  const normalized = slug.trim().toLowerCase();
  const mapping = LEAGUE_SLUGS.find(m => m.slug.toLowerCase() === normalized);
  return mapping?.displayName || null;
}

/**
 * Check if a slug is valid
 */
export function isValidSlug(slug: string): boolean {
  if (!slug) return false;
  const lower = slug.toLowerCase();
  if (RESERVED_SLUGS.has(lower)) return false;
  if (!/^[a-z0-9-]+$/i.test(slug)) return false;
  return true;
}

/**
 * Get all available slugs
 */
export function getAllSlugs(): string[] {
  return LEAGUE_SLUGS.map(m => m.slug);
}
